<template>
  <div class="menstrual-tracker">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to access your personal menstrual cycle tracking data and get personalized predictions.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else class="row">
      <!-- Main Calendar Area -->
      <div class="col-lg-8">
        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <i class="bi bi-calendar-heart me-2 text-danger"></i>Menstrual Cycle Calendar
            </h5>
            <button class="btn btn-primary btn-sm" @click="showRecordForm = true">
              <i class="bi bi-plus-lg me-1"></i>Add Record
            </button>
          </div>
          <div class="card-body">
            <!-- Calendar Grid -->
            <CalendarGrid
              :current-date="currentDate"
              :records="records"
              :predictions="predictions"
              @prev-month="previousMonth"
              @next-month="nextMonth"
              @day-click="handleDayClick"
            />
          </div>
        </div>

        <!-- History Records -->
        <RecordHistory
          :records="records"
          @edit-record="handleEditRecord"
          @delete-record="handleDeleteRecord"
          @export-data="handleExportData"
          @email-data="handleEmailData"
        />
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Settings Panel -->
        <SettingsPanel
          :settings="settings"
          :predictions="predictions"
          :records-count="records.length"
          @update-setting="handleUpdateSetting"
          @save-settings="handleSaveSettings"
        />
      </div>
    </div>

    <!-- Record Form Modal -->
    <RecordForm
      v-if="showRecordForm"
      :record="editingRecord"
      :edit-mode="!!editingRecord"
      @close="closeRecordForm"
      @save="handleSaveRecord"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { useToast } from '@/composables/useToast'
import { useConfirm } from '@/composables/useConfirm'
import { sendMenstrualRecordsCSV } from '@/services/emailService'
import CalendarGrid from './menstrual-tracker/CalendarGrid.vue'
import RecordHistory from './menstrual-tracker/RecordHistory.vue'
import SettingsPanel from './menstrual-tracker/SettingsPanel.vue'
import RecordForm from './menstrual-tracker/RecordForm.vue'
import {
  addMenstrualRecord,
  updateMenstrualRecord,
  deleteMenstrualRecord,
  getUserMenstrualRecords,
  getUserSettings,
  updateUserSettings,
  calculateCyclePredictions,
  exportToCSV,
  downloadCSV
} from '@/services/menstrualService'

export default {
  name: 'MenstrualTracker',
  components: {
    CalendarGrid,
    RecordHistory,
    SettingsPanel,
    RecordForm
  },
  setup() {
    const { user, isAuthenticated } = useFirebaseAuth()
    const toast = useToast()
    const { confirm } = useConfirm()

    // State
    const currentDate = ref(new Date())
    const records = ref([])
    const settings = ref({
      remind_period: true,
      remind_ovulation: true,
      email_notification: false,
      reminder_days_before: 3
    })
    const showRecordForm = ref(false)
    const editingRecord = ref(null)
    const loading = ref(false)

    // Computed
    const currentUserId = computed(() => user.value?.uid)

    const predictions = computed(() => {
      return calculateCyclePredictions(records.value)
    })

    // Methods
    const loadRecords = async () => {
      if (!currentUserId.value) return

      try {
        loading.value = true
        const data = await getUserMenstrualRecords(currentUserId.value, 6)
        records.value = data
      } catch (error) {
        console.error('Error loading records:', error)
        toast.error('Failed to load records')
      } finally {
        loading.value = false
      }
    }

    const loadSettings = async () => {
      if (!currentUserId.value) return

      try {
        const data = await getUserSettings(currentUserId.value)
        settings.value = data
      } catch (error) {
        console.error('Error loading settings:', error)
      }
    }

    const handleSaveRecord = async (formData) => {
      if (!currentUserId.value) return

      try {
        if (editingRecord.value) {
          // Update existing record
          await updateMenstrualRecord(editingRecord.value.id, formData)
          toast.success('Record updated successfully!')
        } else {
          // Add new record
          await addMenstrualRecord(currentUserId.value, formData)
          toast.success('Record added successfully!')
        }

        await loadRecords()
        closeRecordForm()
      } catch (error) {
        console.error('Error saving record:', error)
        toast.error('Failed to save record')
      }
    }

    const handleEditRecord = (record) => {
      editingRecord.value = record
      showRecordForm.value = true
    }

    const handleDeleteRecord = async (recordId) => {
      const confirmed = await confirm('Are you sure you want to delete this record?')
      if (!confirmed) return

      try {
        await deleteMenstrualRecord(recordId)
        toast.success('Record deleted successfully!')
        await loadRecords()
      } catch (error) {
        console.error('Error deleting record:', error)
        toast.error('Failed to delete record')
      }
    }

    const handleExportData = () => {
      try {
        const csvContent = exportToCSV(records.value)
        const filename = `menstrual_records_${new Date().toISOString().split('T')[0]}.csv`
        downloadCSV(csvContent, filename)
        toast.success('Data exported successfully!')
      } catch (error) {
        console.error('Error exporting data:', error)
        toast.error('Failed to export data')
      }
    }

    const handleEmailData = async () => {
      try {
        if (!user.value?.email) {
          toast.error('User email not found. Please login again.')
          return
        }

        if (records.value.length === 0) {
          toast.error('No records to export')
          return
        }

        // Show loading toast
        toast.info('Sending email with CSV attachment...')

        // Generate CSV content
        const csvContent = exportToCSV(records.value)

        // Convert CSV to Base64
        const csvBase64 = btoa(unescape(encodeURIComponent(csvContent)))

        // Generate filename
        const filename = `menstrual_records_${new Date().toISOString().split('T')[0]}.csv`

        // Get user name
        const userName = user.value.displayName || user.value.email.split('@')[0]

        // Send email
        const result = await sendMenstrualRecordsCSV(
          user.value.email,
          userName,
          csvBase64,
          filename
        )

        if (result.success) {
          toast.success(`CSV file sent successfully to ${user.value.email}!`)
        } else {
          toast.error(result.error || 'Failed to send email')
        }
      } catch (error) {
        console.error('Error sending email:', error)
        toast.error('Failed to send email. Please try again.')
      }
    }

    const handleUpdateSetting = (key, value) => {
      settings.value[key] = value
    }

    const handleSaveSettings = async () => {
      if (!currentUserId.value) return

      try {
        await updateUserSettings(currentUserId.value, settings.value)
        toast.success('Settings saved successfully!')
      } catch (error) {
        console.error('Error saving settings:', error)
        toast.error('Failed to save settings')
      }
    }

    const handleDayClick = (day) => {
      // Open record form for the selected day
      const existingRecord = records.value.find(record => {
        const startDate = record.start_date?.toDate?.() || new Date(record.start_date)
        const endDate = record.end_date ? (record.end_date?.toDate?.() || new Date(record.end_date)) : startDate

        return day.date >= startDate && day.date <= endDate
      })

      if (existingRecord) {
        handleEditRecord(existingRecord)
      } else {
        editingRecord.value = null
        showRecordForm.value = true
      }
    }

    const closeRecordForm = () => {
      showRecordForm.value = false
      editingRecord.value = null
    }

    const previousMonth = () => {
      currentDate.value = new Date(
        currentDate.value.getFullYear(),
        currentDate.value.getMonth() - 1,
        1
      )
    }

    const nextMonth = () => {
      currentDate.value = new Date(
        currentDate.value.getFullYear(),
        currentDate.value.getMonth() + 1,
        1
      )
    }

    // Watch for authentication changes
    watch(isAuthenticated, (authenticated) => {
      if (authenticated) {
        loadRecords()
        loadSettings()
      }
    }, { immediate: true })

    // Load data on mount if authenticated
    onMounted(() => {
      if (isAuthenticated.value) {
        loadRecords()
        loadSettings()
      }
    })

    return {
      isAuthenticated,
      user,
      currentDate,
      records,
      settings,
      predictions,
      showRecordForm,
      editingRecord,
      loading,
      handleSaveRecord,
      handleEditRecord,
      handleDeleteRecord,
      handleExportData,
      handleEmailData,
      handleUpdateSetting,
      handleSaveSettings,
      handleDayClick,
      closeRecordForm,
      previousMonth,
      nextMonth
    }
  }
}
</script>

<style scoped>
.menstrual-tracker {
  min-height: 500px;
}

.btn-primary {
  background: linear-gradient(135deg, #E91E63 0%, #9C27B0 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c2185b 0%, #7b1fa2 100%);
}

@media (max-width: 992px) {
  .col-lg-4 {
    margin-top: 1rem;
  }
}
</style>
